import React, { useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableRow
} from "@material-ui/core";
import { X, Camera, Upload, Receipt } from "lucide-react";
import Tesseract from "tesseract.js";
import config from "../config";
import ContinuePopup from "./ContinuePopup";

export default function ScannerDialog({ open, onClose, onConfirm }) {
    const fileInputRef = useRef();
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [extractedText, setExtractedText] = useState("");
    const [parsedItems, setParsedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [ocrError, setOcrError] = useState("");
    const [showContinuePopup, setShowContinuePopup] = useState(false);


    const resetState = () => {
        setImagePreview(null);
        setSelectedFile(null);
        setExtractedText("");
        setParsedItems([]);
        setIsLoading(false);
        setOcrError("");
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            setSelectedFile(file);
            setIsLoading(true);
            setExtractedText("");
            setParsedItems([]);
            setOcrError("");

            const result = await Tesseract.recognize(file, "eng", {
                logger: (m) => console.log(m),
            });

            const text = result.data.text.trim();
            setExtractedText(text);

            const parsed = parseItemsFromText(text);
            const cleaned = filterDuplicateSummaries(parsed);

            // Error handling: if no OCR text or no items, show error and reset
            if (!text || cleaned.length === 0) {
                setIsLoading(false);
                setOcrError("Could not extract items from the bill. Please upload a clearer image or try again.");
                setParsedItems([]);
                return;
            }

            setParsedItems(cleaned);
            setIsLoading(false);
        }
    };

    const handleCameraClick = () => {
        handleClose();
        alert("Camera feature not implemented yet.");
    };

    const toBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };


    const handleConfirm = async () => {
        if (!selectedFile) return;

        // Filter and clean items
        const seen = new Set();
        const items = (parsedItems || [])
            .filter(i => i.type === 'item')
            .map(({ product, price }) => ({
                product: product.replace(/\s+\d+$/, '').trim(),
                price
            }))
            .filter(({ product }) => {
                const lower = product.toLowerCase();
                if (seen.has(lower)) return false;
                seen.add(lower);
                return true;
            });

        const totalsArray = (parsedItems || []).filter(i => i.type === 'summary');

        if (!extractedText || items.length === 0) {
            alert("Could not extract items from the bill. Please upload a clearer image or try again.");
            return;
        }

        const totalPrice =
            totalsArray.find(t => /total|amount due|balance/i.test(t.product))?.price || 0;

        try {
            const requestParams = {
                fsAction: "uploadScannedBill",
                fsFirstImage: await toBase64(selectedFile),
                items,
                totals: totalPrice
            };
            console.log("Request Payload:", requestParams);

            const responseJson = await config.fetchInformation(requestParams);

            if (responseJson.fbIsError === true) {
                // show error
            } else {
                if (onConfirm) onConfirm();
                setShowContinuePopup(true);
            }

            handleClose();
        } catch (error) {
            console.error("Error converting file:", error);
        }
    };



    const parseItemsFromText = (text) => {
        const rawLines = text.split("\n").map(line => line.trim()).filter(Boolean);
        const lines = [];

        for (let i = 0; i < rawLines.length; i++) {
            const current = rawLines[i];
            const next = rawLines[i + 1] || '';
            if (
                /(TOTAL|DUE|BALANCE|Subtotal|Tax||DiscountDeposit|Other)/i.test(current) &&
                /^-?[^\d]*\d+\.\d{2}$/.test(next) &&
                !/([a-zA-Z]+\s*){2,}/.test(next)
            ) {
                lines.push(`${current} ${next}`);
                i++;
            } else {
                lines.push(current);
            }
        }

        const items = [];
        const amountOnlyRegex = /^-?\d+\.\d{2}$/;
        const summaryKeywords = ["TOTAL", "DUE", "BALANCE", "Tax", "Subtotal", "Discount", "Deposit", "Other"];
        let tempDesc = "";

        for (let line of lines) {
            const cleanLine = line.replace(/[$₹£]/g, '').trim();

            if (amountOnlyRegex.test(cleanLine)) {
                const amount = parseFloat(cleanLine);
                if (tempDesc) {
                    const cleanedProduct = tempDesc.replace(/\s*\d+(\.\d{2})?$/, '').replace(/\s+\d+$/, '').trim(); // 🔧 Fix
                    items.push({ product: cleanedProduct, price: amount, type: 'item' });
                    tempDesc = "";
                }
            } else if (/-?\d+\.\d{2}$/.test(cleanLine)) {
                const match = cleanLine.match(/^(.*?)(-?\d+\.\d{2})$/);
                if (match) {
                    let [, desc, amt] = match;
                    desc = desc.replace(/\s*\d+$/, '').trim(); // 🔧 Fix quantity-only suffix
                    const isSummary = summaryKeywords.some(k => desc.toLowerCase().includes(k.toLowerCase()));
                    const cleanedProduct = desc.replace(/\s*\d+(\.\d{2})?$/, '').trim(); // 🔧 Fix price suffix
                    items.push({
                        product: isSummary ? formatSummaryLabel(desc) : cleanedProduct,
                        price: parseFloat(amt),
                        type: isSummary ? 'summary' : 'item'
                    });
                }
            } else {
                tempDesc += line.trim() + " ";
            }
        }


        return items;
    };

    const formatSummaryLabel = (desc) => {
        const map = {
            total: "Total",
            "amount due": "Amount Due",
            "total due": "Total Due",
            balance: "Balance",
            "tax due": "Tax",
            tax: "Tax",
            subtotal: "Subtotal",
            discount: "Discount",
            deposit: "Deposit",
            other: "Other"
        };
        const lower = desc.toLowerCase();
        for (const key in map) {
            if (lower.includes(key)) return map[key];
        }
        return desc.trim();
    };

    const filterDuplicateSummaries = (items) => {
        const seen = new Set();
        const result = [];

        for (let item of items) {
            const lowerLabel = item.product.toLowerCase();

            const isSummary = ['total', 'discount', 'tax', 'deposit', 'balance', 'amount due'].some(k =>
                lowerLabel.includes(k)
            );

            if (isSummary) {
                if (!seen.has(lowerLabel)) {
                    seen.add(lowerLabel);
                    result.push(item);
                }
            } else {
                result.push(item);
            }
        }

        return result;
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                aria-labelledby="scanner-dialog-title"
                aria-describedby="scanner-dialog-description"
            >
                <div className="mobyPopup billUploadSelectionPopup">
                    <DialogTitle id="scanner-dialog-title">
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" style={{ fontWeight: 700, fontSize: "1.2rem", color: "black" }}>
                                Bill Scanner
                            </Typography>
                            <IconButton onClick={handleClose} size="small" style={{ position: "absolute", right: 8, top: 8 }}>
                                <X size={15} />
                            </IconButton>
                        </Box>
                    </DialogTitle>

                    <DialogContent>
                        <DialogContentText id="scanner-dialog-description">
                            {!imagePreview && (
                                <>
                                    <Box display="flex" justifyContent="center" alignItems="center">
                                        <div className="bill-scanner-icon">
                                            <Receipt color="white" size={28} strokeWidth={2.2} />
                                        </div>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        style={{ fontWeight: 600, fontSize: "1.05rem", color: "black", marginTop: 12 }}
                                    >
                                        Choose Scanning Method
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ color: "black", opacity: 0.6, marginBottom: 16, fontSize: '14px' }}
                                    >
                                        Select how you want to capture your bill
                                    </Typography>
                                    <Box className="bill-scanner-actions">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            ref={fileInputRef}
                                            name="fsFirstImage"
                                            onChange={handleFileChange}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleUploadClick}
                                            startIcon={<Upload size={18} />}
                                            fullWidth
                                            style={{ marginBottom: 12 }}
                                        >
                                            Upload From Gallery
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleCameraClick}
                                            startIcon={<Camera size={18} />}
                                            fullWidth
                                            className="mt-0"
                                        >
                                            Take Camera Photo
                                        </Button>
                                    </Box>
                                </>
                            )}

                            {imagePreview && (
                                <Box textAlign="center">
                                    <img
                                        src={imagePreview}
                                        alt="Bill Preview"
                                        style={{ maxWidth: "100%", maxHeight: 250, borderRadius: 6 }}
                                    />
                                </Box>
                            )}

                            {isLoading && (
                                <Box mt={2} display="flex" justifyContent="center">
                                    <CircularProgress size={24} />
                                    <Typography style={{ marginLeft: 10 }}>Reading text...</Typography>
                                </Box>
                            )}

                            {imagePreview && ocrError && !isLoading && (
                                <Box mt={2} mb={2} textAlign="center">
                                    <Typography variant="body2" style={{ color: 'red', fontWeight: 600, fontSize: '12px' }}>
                                        {ocrError}
                                    </Typography>
                                </Box>
                            )}

                            {!isLoading && parsedItems.length > 0 && (
                                <Box mt={3}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                                        📋 Invoice Items:
                                    </Typography>

                                    <Box
                                        style={{
                                            backgroundColor: "#fafafa",
                                            padding: 10,
                                            borderRadius: 6,
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                                        }}
                                    >
                                        <Table size="small">
                                            <TableBody>
                                                {parsedItems.map((item, index) => {
                                                    const isSummary = ['total', 'subtotal', 'tax', 'amount due', 'balance', 'discount'].some(k =>
                                                        item.product.toLowerCase().includes(k)
                                                    );

                                                    return (
                                                        <TableRow
                                                            key={index}
                                                            style={{
                                                                borderBottom: "1px solid #eee",
                                                                backgroundColor: isSummary ? "#f0f0f0" : "transparent",
                                                            }}
                                                        >
                                                            <TableCell
                                                                style={{
                                                                    fontWeight: isSummary ? 600 : 400,
                                                                    fontSize: "0.95rem",
                                                                    borderBottom: "none",
                                                                    padding: "6px 8px",
                                                                }}
                                                            >
                                                                {item.product}
                                                            </TableCell>
                                                            <TableCell
                                                                align="right"
                                                                style={{
                                                                    fontWeight: isSummary ? 600 : 400,
                                                                    fontSize: "0.95rem",
                                                                    borderBottom: "none",
                                                                    padding: "6px 8px",
                                                                }}
                                                            >
                                                                ₹{item.price.toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Box>
                            )}

                        </DialogContentText>
                    </DialogContent>

                    <DialogActions>
                        {imagePreview && !isLoading && ocrError ? (
                            <Button
                                className="uploadNowBtn"
                                onClick={handleClose}
                                variant="contained"
                                color="secondary"
                                fullWidth
                            >
                                Cancel
                            </Button>
                        ) : (
                            imagePreview && !isLoading && (
                                <Button
                                    className="uploadNowBtn"
                                    onClick={handleConfirm}
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    Proceed
                                </Button>
                            )
                        )}
                    </DialogActions>

                </div>
            </Dialog>
            {showContinuePopup && (
                <ContinuePopup
                    popupButtonText="OK"
                    popupTitle="MyCash success"
                    popupContent="Your bill has been successfully scanned and processed. You can view it in your wallet."
                    handleClose={() => {
                        setShowContinuePopup(false);
                        handleClose(); // Close the ScannerDialog afterward
                    }}
                    openDialog={showContinuePopup}
                />
            )}
        </>
    );
}
